import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getPack } from "@/config/horizonscanning/packs";
import { suggestedPublisherIds } from "@/config/horizonscanning/sources";
import { isWorkEmail } from "@/config/horizonscanning/options";
import {
  emptyWizardState,
  normalizeCustomTopic,
  trimCustomTopic,
  type WizardState,
} from "@/config/horizonscanning/types";
import { getHorizonScanningSession, setHorizonScanningSession } from "./sessionKeys";

const STORAGE_KEY = "wizard";

const STEPS = ["topics", "sources", "frequency", "persona", "email"] as const;
export type WizardStepId = (typeof STEPS)[number];

function ensureCustomTopicRows(
  customTopic: ReturnType<typeof normalizeCustomTopic>,
): WizardState["customTopic"] {
  if (!customTopic) return null;
  if (customTopic.terms.length > 0) return customTopic;
  return { ...customTopic, language: customTopic.language || "en", terms: [{ term: "" }] };
}

function loadState(packFromUrl: string): WizardState {
  try {
    const raw = getHorizonScanningSession(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as WizardState & { customTopic?: unknown; sourceIds?: string[] };
      const customTopic = ensureCustomTopicRows(normalizeCustomTopic(parsed.customTopic));
      const publisherIds =
        parsed.publisherIds?.length
          ? parsed.publisherIds
          : suggestedPublisherIds(parsed.sourceIds ?? []);
      if (packFromUrl && parsed.packId !== packFromUrl) {
        const pack = getPack(packFromUrl);
        return {
          ...parsed,
          packId: packFromUrl,
          packVersion: pack?.packVersion ?? "",
          topicIds: [],
          publisherIds: suggestedPublisherIds(pack?.suggestedSourceIds ?? []),
          customTopic,
        };
      }
      return {
        ...parsed,
        packVersion: parsed.packVersion || getPack(parsed.packId)?.packVersion || "",
        publisherIds,
        customTopic,
      };
    }
  } catch {
    /* ignore */
  }
  const packId = packFromUrl || "banking";
  const pack = getPack(packId);
  return {
    ...emptyWizardState(packId),
    packVersion: pack?.packVersion ?? "",
    publisherIds: suggestedPublisherIds(pack?.suggestedSourceIds.slice(0, 1) ?? []),
  };
}

function persist(state: WizardState) {
  setHorizonScanningSession(STORAGE_KEY, JSON.stringify(state));
}

export function useHorizonScanningWizard() {
  const [params] = useSearchParams();
  const packFromUrl = params.get("pack")?.trim() ?? "";
  const [stepIndex, setStepIndex] = useState(0);
  const [state, setState] = useState<WizardState>(() => loadState(packFromUrl));
  const [showErrors, setShowErrors] = useState(false);

  const stepId = STEPS[stepIndex];
  const pack = getPack(state.packId);

  const update = useCallback((patch: Partial<WizardState>) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      persist(next);
      return next;
    });
  }, []);

  const error = useMemo(() => {
    if (stepId === "topics") {
      if (!state.packId) return "Pick an industry pack.";
      if (!state.includeAll && state.topicIds.length === 0 && !state.customTopic) {
        return "Tick at least one topic, add your own, or include all publications.";
      }
      if (state.customTopic) {
        const trimmed = trimCustomTopic(state.customTopic);
        if (!trimmed.title || trimmed.terms.length === 0) {
          return "Give your topic a title and at least one search term.";
        }
      }
    }
    if (stepId === "sources" && state.publisherIds.length === 0) {
      return "Select at least one source (€29 per source per month when you continue).";
    }
    if (stepId === "persona" && !state.persona) return "Please choose one.";
    if (stepId === "email") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim())) return "please use your work address";
      if (!isWorkEmail(state.email)) return "please use your work address";
    }
    return null;
  }, [stepId, state]);

  const next = useCallback(() => {
    if (stepId === "topics" && state.customTopic) {
      const trimmed = trimCustomTopic(state.customTopic);
      const customTopic =
        trimmed.terms.length > 0
          ? trimmed
          : { title: trimmed.title, language: trimmed.language || "en", terms: [{ term: "" }] };
      update({ customTopic });
      if (!trimmed.title || trimmed.terms.length === 0) {
        setShowErrors(true);
        return false;
      }
    }
    if (error) {
      setShowErrors(true);
      return false;
    }
    setShowErrors(false);
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    return stepIndex === STEPS.length - 1;
  }, [error, stepId, state.customTopic, stepIndex, update]);

  const back = useCallback(() => {
    setShowErrors(false);
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  return {
    stepId,
    stepIndex,
    totalSteps: STEPS.length,
    progress: (stepIndex + 1) / STEPS.length,
    state,
    pack,
    update,
    error: showErrors ? error : null,
    canAdvance: !error,
    next,
    back,
    isLast: stepIndex === STEPS.length - 1,
  };
}
